/* KANON `Raporty` — `03-do-przejrzenia.html`: the queue of submissions.
 *
 * ── The open submission is a MODAL now, and its stylesheet is the feature's ──────────────────
 * The canon draws the open one in a 360px pane on the right. It went on 17 September 2026, when the
 * owner asked for the preview to be the client detail's own report in a dialog — so the pane's
 * blocks and the answer lines inside them left this file with it, rather than being kept beside a
 * second rendering of the same submission. The list takes the full width and keeps every reserve it
 * had, so nothing about how it loads changed.
 *
 * ── The list is a card that scrolls itself ──────────────────────────────────────────────────
 * Fit-or-scroll: at 1280x620 five rows and a footer do not fit under the header and the tab strip,
 * so the rows scroll inside the card and the page does not. The window sentence stays put at the
 * bottom, because it is a fact about the whole list rather than about its last row.
 *
 * ── A row is a BUTTON and carries two more ──────────────────────────────────────────────────
 * Pressing the row opens the report dialog; the eye and the check are their own controls. Nested
 * interactive elements are not allowed, so the row's own target is a button that fills the text
 * block and the two controls stand OUTSIDE it as siblings — which is also what puts them in the tab
 * order in the order they are drawn.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* `min-width: 0` because this row is itself a flex ITEM of the tab panel, and a flex item's default
 * `min-width: auto` refuses to shrink below its content. The matrix tab measured what that costs —
 * its grid took the whole row and pushed the card beside it off the screen — and the same line is
 * here because the same shape would do the same thing the first time a row's text ran long. */
.tab {
  display: flex;
  flex: 1;
  gap: var(--space-7);
  min-width: 0;
  min-height: 0;
}

.card {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);

  /* The canon's own 24, where this drew 14 — `03-do-przejrzenia.html` wears the export's `.card`. */
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
}

.list {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: var(--h-report-queue-min);
  margin: var(--space-0);
  padding: var(--space-0);
  overflow-y: auto;
  list-style: none;
}

/* ── a row ────────────────────────────────────────────────────────────────────────────────── */

.row {
  display: flex;
  flex: none;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-report-queue-row);

  /* The canon's own `14px 18px`. This drew 17 on the inline axis. */
  padding: var(--space-14px) var(--space-5);
  border-top: var(--border-hairline) solid var(--border);

  /* The canon's 36px disc, through the one seam `shared/ui/AvatarChip` opens. */
  --sz-avatar: var(--sz-report-queue-face);
}

.row:first-child {
  border-top: none;
}

.rowOn {
  background: var(--hover-tint);
}

/* The row's own target: the person, the form and the one-line preview. Transparent and unbordered —
 * the row it fills is the drawn object, and a second box around the text would be a control inside
 * a control. */
.rowButton {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3px);
  min-width: 0;
  padding: var(--space-0);
  font: inherit;
  color: inherit;
  text-align: start;
  cursor: pointer;
  background: none;
  border: none;
}

.rowButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.rowTop {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  min-width: 0;
}

/* The canon's 14 / 700, where this drew 12. */
.rowName {
  overflow: hidden;
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowForm {
  overflow: hidden;
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The canon's 12.5, where this drew 10. */
.rowSummary {
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The fixed track the date stands in, so five rows' dates line up down the list whatever their
 * names are. */
.rowWhen {
  display: inline-flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  width: var(--w-report-queue-when);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.rowWhenLate {
  color: var(--text-2);
}

/* The canon's ember pip on a submission that came on time. It is the second channel only — the word
 * „spóźniona" is what carries lateness, and this marks nothing on its own. */
.pip {
  flex: none;
  width: 7px;
  height: 7px;
  background: var(--accent);
  border-radius: var(--r-circle);
}

/* The canon's `.ibtn`: 28 at radius 9, where this drew 9.5. */
.rowControl {
  width: var(--sz-report-ibtn);
  height: var(--sz-report-ibtn);
  font-size: var(--fs-micro);
  background: transparent;
  border-radius: var(--radius-mark);
}

.rowControlIcon {
  width: 14px;
  height: 14px;
}

/* ── the foot ─────────────────────────────────────────────────────────────────────────────── */

/* „Kolejka sięga 30 dni wstecz" — the one sentence that says what this queue is NOT showing, so it
 * is read rather than glanced at and takes `--text-2`. The tertiary ink at `--fs-label-sm` does not
 * reach 4.5:1 in either theme. */
.foot {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2px);
  padding: var(--space-12) var(--space-5);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text-2);
  border-top: var(--border-hairline) solid var(--border);
}

/* The footer reserves both lines from the first frame. The window sentence and the truncation note
 * both arrive with the read, and a foot that grew a line when they did would move the card's own
 * bottom edge — which is the whole of the no-jump policy on this tab. */
.footLine {
  min-height: 17px;
}

.failed {
  font-weight: var(--fw-bold);
  color: var(--danger-text-strong);
}

/* ── the states ───────────────────────────────────────────────────────────────────────────── */

.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: var(--h-report-queue-min);
  padding: var(--space-7) var(--space-5);
  text-align: center;
}

.skelRow {
  display: flex;
  flex: none;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-report-queue-row);
  padding: var(--space-14px) var(--space-5);
  border-top: var(--border-hairline) solid var(--border);
}

.skelRow:first-child {
  border-top: none;
}

.skelFace {
  flex: none;
  width: var(--sz-report-queue-face);
  height: var(--sz-report-queue-face);
  border-radius: var(--r-circle);
}

.skelText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6px);
  min-width: 0;
}

.skelName {
  width: 176px;
  height: 12px;
}

.skelSummary {
  width: 232px;
  height: 11px;
}

.skelWhen {
  flex: none;
  width: 120px;
  height: 11px;
}
